<script setup>
import { computed } from 'vue';
import { useLocale } from 'forms/composables/useLocale';

const { t } = useLocale();

const props = defineProps({
  field: { type: Object, required: true },
  // { [docKey]: { [partKey|'_']: File } }
  modelValue: { type: Object, default: () => ({}) },
});

const emit = defineEmits(['update:modelValue']);

// Resolved required-document list embedded by the backend (subcategory templates the
// field opts into + its ad-hoc additional documents), each { key, label, required, parts }.
const documents = computed(() =>
  Array.isArray(props.field.documents) ? props.field.documents : []
);

// File pickers accept the same formats the document-request backend allows.
const ACCEPT = '.pdf,.png,.jpg,.jpeg,.webp,.heic,.doc,.docx,.xls,.xlsx';

// A multi-part document renders one slot per part; a single-file document renders one
// slot under the "_" placeholder key (matching the submit serialization).
function slotsFor(doc) {
  return Array.isArray(doc.parts) && doc.parts.length
    ? doc.parts
    : [{ key: '_', label: '' }];
}

function fileFor(docKey, partKey) {
  return props.modelValue?.[docKey]?.[partKey] || null;
}

function setFile(docKey, partKey, file) {
  const next = { ...(props.modelValue || {}) };
  const slot = { ...(next[docKey] || {}) };
  if (file) {
    slot[partKey] = file;
  } else {
    delete slot[partKey];
  }
  next[docKey] = slot;
  emit('update:modelValue', next);
}

function onFileChange(docKey, partKey, event) {
  const file = event.target.files?.[0];
  if (file) setFile(docKey, partKey, file);
  // Clear so re-picking the same file fires change again.
  event.target.value = '';
}
</script>

<template>
  <div class="space-y-3">
    <div
      v-for="doc in documents"
      :key="doc.key"
      class="border border-slate-200 rounded-lg p-3 bg-slate-50"
    >
      <p class="text-sm font-medium text-slate-700 mb-2">
        {{ doc.label }}
        <span v-if="doc.required" class="text-red-400">*</span>
      </p>

      <div class="space-y-2">
        <div
          v-for="slot in slotsFor(doc)"
          :key="slot.key"
          class="flex items-center gap-2"
        >
          <span
            v-if="slot.label"
            class="text-xs text-slate-500 w-20 shrink-0 truncate"
          >
            {{ slot.label }}
          </span>

          <!-- File selected: name + remove -->
          <div
            v-if="fileFor(doc.key, slot.key)"
            class="flex items-center gap-2 flex-1 min-w-0 bg-white border border-slate-200 rounded-lg px-3 py-2"
          >
            <svg
              class="w-4 h-4 text-slate-400 flex-shrink-0"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13"
              />
            </svg>
            <span class="text-xs text-slate-700 truncate flex-1">
              {{ fileFor(doc.key, slot.key).name }}
            </span>
            <button
              type="button"
              class="p-1 text-slate-400 hover:text-red-500 transition-colors"
              :aria-label="t('DOCUMENT_REMOVE_LABEL')"
              @click="setFile(doc.key, slot.key, null)"
            >
              <svg
                class="w-3.5 h-3.5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>

          <!-- Empty slot: upload button -->
          <label
            v-else
            class="flex-1 min-w-0 flex items-center justify-center gap-2 cursor-pointer bg-white border border-dashed border-slate-300 rounded-lg px-3 py-2 text-xs text-slate-600 hover:border-woot-400 transition-colors"
          >
            <svg
              class="w-4 h-4 text-slate-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="1.5"
                d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
              />
            </svg>
            <span class="text-woot-500 font-medium">
              {{ t('DOCUMENT_SELECT_LABEL') }}
            </span>
            <input
              type="file"
              :accept="ACCEPT"
              class="hidden"
              @change="onFileChange(doc.key, slot.key, $event)"
            />
          </label>
        </div>
      </div>
    </div>

    <p v-if="!documents.length" class="text-xs text-slate-400">
      {{ t('DOCUMENT_NONE') }}
    </p>
  </div>
</template>
